import { useState, useEffect } from "react";
import {
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  Plus,
  Eye,
  EyeOff,
} from "lucide-react";
import { SiZapier } from "react-icons/si";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface ZapierIntegrationCardProps {
  title?: string;
  description?: string;
  compact?: boolean;
}

const ZapierIntegrationCard = ({
  title = "Zapier Integration",
  description = "Automate workflows when conversations are completed",
  compact = false,
}: ZapierIntegrationCardProps) => {
  const [apiKey, setApiKey] = useState<string>("");
  const [apiKeyPrefix, setApiKeyPrefix] = useState<string>("");
  const [hasApiKey, setHasApiKey] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [showFullKey, setShowFullKey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const { organization } = useAuth();
  const { toast } = useToast();

  // Key for localStorage to temporarily store the API key
  const getStorageKey = () =>
    organization?.id ? `zapier_api_key_${organization.id}` : null;

  useEffect(() => {
    if (organization?.id) {
      loadApiKey();
    }
  }, [organization]);

  const loadApiKey = async () => {
    if (!organization?.id) return;

    setIsLoading(true);
    try {
      // First check if we have a stored key in localStorage
      const storageKey = getStorageKey();
      const storedKey = storageKey ? localStorage.getItem(storageKey) : null;

      console.log("Loading API key:", {
        storageKey,
        storedKey: storedKey ? "exists" : "none",
      });

      const { data, error } = await supabase.rpc("get_organization_api_keys", {
        org_id: organization.id,
      });

      if (error) {
        console.error("Error loading API keys:", error);
        return;
      }

      console.log("API keys from database:", data);

      if (data && data.length > 0) {
        const activeKey = data.find((key: any) => key.is_active);
        if (activeKey) {
          console.log("Active key found:", activeKey);
          setApiKeyPrefix(activeKey.key_prefix);
          setHasApiKey(true);

          // If we have a stored key that matches the prefix, use it
          if (storedKey) {
            const prefixWithoutDots = activeKey.key_prefix.replace("...", "");
            console.log("Checking stored key match:", {
              storedKeyPrefix: storedKey.substring(0, 12),
              dbPrefix: prefixWithoutDots,
              matches: storedKey.startsWith(prefixWithoutDots),
            });

            if (storedKey.startsWith(prefixWithoutDots)) {
              console.log("✅ Restored API key from localStorage");
              setApiKey(storedKey);
            } else {
              console.log("❌ Stored key does not match current key prefix");
              // Clear invalid stored key
              localStorage.removeItem(storageKey);
            }
          } else {
            console.log("No stored key found in localStorage");
          }
        }
      } else {
        console.log("No API keys found in database");
      }
    } catch (error) {
      console.error("Error loading API keys:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateApiKey = async () => {
    if (isGenerating) return; // Prevent concurrent requests
    if (!organization?.id) return;

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.rpc("generate_api_key", {
        org_id: organization.id,
        key_name: "Zapier Integration Key",
      });

      if (error) {
        console.error("Error generating API key:", error);
        toast({
          variant: "destructive",
          title: "Failed to generate API key",
          description: "Please try again.",
        });
        return;
      }

      if (data && data.length > 0) {
        const newKey = data[0];
        setApiKey(newKey.api_key);
        setApiKeyPrefix(newKey.key_prefix);
        setHasApiKey(true);
        setShowFullKey(true); // Show the full key when freshly generated

        // Store the key temporarily in localStorage for convenience
        const storageKey = getStorageKey();
        if (storageKey) {
          localStorage.setItem(storageKey, newKey.api_key);
          console.log("✅ Stored API key in localStorage:", {
            storageKey,
            keyPrefix: newKey.key_prefix,
            keyStored: true,
          });
        }

        toast({
          title: "API key generated!",
          description:
            "Copy this key now - it will remain accessible from this page until you regenerate it.",
        });
      }
    } catch (error) {
      console.error("Error generating API key:", error);
      toast({
        variant: "destructive",
        title: "Failed to generate API key",
        description: "Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const regenerateApiKey = async () => {
    if (isGenerating) return; // Prevent concurrent requests
    if (!organization?.id) return;

    if (
      !confirm(
        "Are you sure? This will invalidate your current API key and any existing Zapier integrations will stop working until you update them.",
      )
    ) {
      return;
    }

    // Clear the old stored key
    const storageKey = getStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }

    await generateApiKey();
  };

  const handleCopyApiKey = async () => {
    try {
      if (!apiKey) {
        toast({
          variant: "destructive",
          title: "Key not available",
          description:
            "For security, the full key is only accessible after generation. Please regenerate to copy.",
        });
        return;
      }

      setIsCopying(true);
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast({
        title: "API Key copied!",
        description: "You can now paste this key in Zapier.",
      });

      // Reset the copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Please manually copy the API key.",
      });
    } finally {
      setIsCopying(false);
    }
  };

  const toggleKeyVisibility = () => {
    setShowFullKey(!showFullKey);
  };

  const getDisplayValue = () => {
    if (apiKey && showFullKey) {
      return apiKey;
    }
    if (apiKey && !showFullKey) {
      return (
        apiKey.substring(0, 12) + "••••••••••••••••••••••••••••••••••••••••••••"
      );
    }
    if (apiKeyPrefix) {
      return apiKeyPrefix + "••••••••••••••••••••••••••••••••••••••••••••";
    }
    return "";
  };

  const getStatusMessage = () => {
    if (hasApiKey && apiKey) {
      return (
        <p className="text-green-600 flex items-center gap-1">
          <Check className="h-3 w-3" />
          API key is ready for use in Zapier.
        </p>
      );
    }
    if (hasApiKey && !apiKey) {
      return (
        <p className="text-amber-600 flex items-start gap-1">
          <span className="text-amber-500 text-xs mt-0.5">⚠</span>
          <span>
            API key exists but is not accessible. For security, the full key is
            only shown after generation. Click "Regenerate Key" to copy it.
          </span>
        </p>
      );
    }
    return (
      <p className="text-muted-foreground">
        No API key generated yet. Create one to start using Zapier integrations.
      </p>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className={compact ? "pb-4" : ""}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-50 rounded-lg">
            <SiZapier className="h-6 w-6 text-[#FF4A00]" />
          </div>
          <div>
            <CardTitle className={compact ? "text-base" : "text-lg"}>
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={compact ? "space-y-4" : "space-y-6"}>
        {/* API Key Section */}
        <div className="space-y-3">
          <Label htmlFor="api-key" className="text-sm font-medium">
            API Key for Zapier
          </Label>

          {!hasApiKey ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Generate an API key to connect your Zapier integrations.
              </p>
              <Button
                onClick={generateApiKey}
                disabled={isGenerating}
                className="flex items-center gap-2"
                size="sm"
              >
                {isGenerating ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {isGenerating ? "Generating..." : "Generate API Key"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  id="api-key"
                  type={showFullKey ? "text" : "password"}
                  value={getDisplayValue()}
                  readOnly
                  className="flex-1 font-mono text-sm"
                  placeholder="API key"
                />

                {/* Show/Hide toggle for existing keys */}
                {hasApiKey && (
                  <Button
                    onClick={toggleKeyVisibility}
                    variant="outline"
                    size="sm"
                    className="px-3"
                    title={showFullKey ? "Hide key" : "Show key"}
                  >
                    {showFullKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                )}

                {/* Copy button - always available when we have a key */}
                {hasApiKey && (
                  <Button
                    onClick={handleCopyApiKey}
                    variant="outline"
                    size="sm"
                    className="px-3"
                    disabled={isCopying}
                    title="Copy API key"
                  >
                    {isCopying ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={regenerateApiKey}
                  disabled={isGenerating}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {isGenerating ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {isGenerating ? "Generating..." : "Regenerate Key"}
                </Button>
              </div>

              {/* Status messages */}
              <div className="text-xs">{getStatusMessage()}</div>
            </div>
          )}
        </div>

        {!compact && (
          <>
            {/* What You Can Achieve */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 className="font-medium text-primary mb-3">
                What You Can Achieve with Zapier
              </h4>
              <p className="text-sm text-gray-700 mb-4">
                Automatically trigger actions when conversations are completed.
                Connect FirmAgent to over 7,000+ apps and streamline your
                workflow.
              </p>

              <div className="space-y-3">
                <h5 className="font-medium text-primary text-sm">
                  Popular Integration Examples:
                </h5>
                <div className="grid gap-3 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>
                      <strong>CRM Integration:</strong> Automatically create
                      leads in Salesforce, HubSpot, or Pipedrive when
                      conversations complete
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>
                      <strong>Team Notifications:</strong> Send alerts to Slack
                      or Microsoft Teams when high-priority conversations finish
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>
                      <strong>Email Marketing:</strong> Add leads to Mailchimp,
                      ConvertKit, or ActiveCampaign mailing lists
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>
                      <strong>Project Management:</strong> Create tasks in
                      Asana, Trello, or Monday.com for follow-up actions
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>
                      <strong>Data Storage:</strong> Save conversation data to
                      Google Sheets, Airtable, or databases
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3">
              <h4 className="font-medium">Available Triggers</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-green-100 rounded">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-sm">
                      Conversation Completed
                    </h5>
                    <p className="text-xs text-muted-foreground mt-1">
                      Triggers when a conversation is marked as completed.
                      Includes conversation details, agent information, client
                      data, form fields, and discovered values.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {compact && (
          <div className="text-sm text-muted-foreground">
            <p>
              Connect to 7,000+ apps including Slack, HubSpot, Salesforce,
              Google Sheets, and more. Automatically trigger actions when
              conversations are completed.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={() =>
              window.open(
                "https://zapier.com/developer/public-invite/225950/e2f8205a16a77a0e0f88648b2e8a8f04/",
                "_blank",
              )
            }
            className="flex items-center gap-2"
            size={compact ? "sm" : "default"}
          >
            <ExternalLink className="h-4 w-4" />
            Access Zapier Integration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ZapierIntegrationCard;
